import React, {
  FC,
  PropsWithChildren,
  useState,
  useCallback,
  useEffect,
  useRef,
  useMemo,
} from "react"
import { useQuery, useQueryClient, useMutation } from "@tanstack/react-query"
import Box from "@mui/material/Box"
import Chip from "@mui/material/Chip"
import Button from "@mui/material/Button"
import Typography from "@mui/material/Typography"

import Loading from "../components/system/Loading"
import ErrorComponent from "../components/system/Error"
import AdminShell, { AdminPane, PaneStatus } from "../components/video/AdminShell"
import { ink } from "../components/video/adminTokens"
import VideoDetailsSection from "../components/video/VideoDetailsSection"
import StreamUrlsSection from "../components/video/StreamUrlsSection"
import FourViewSection from "../components/video/FourViewSection"
import StartPositionSection from "../components/video/StartPositionSection"
import { VideoLevelFields, shouldCommitVideoFields, isVideoSealed } from "../components/video/videoFields"
import DiarizationSection from "../components/video/DiarizationSection"
import EmbedCodeSection from "../components/video/EmbedCodeSection"
import JobHistorySection from "../components/video/JobHistorySection"
import TranslationsSection from "../components/video/TranslationsSection"
import AddendumSection from "../components/video/AddendumSection"
import PublicationSection from "../components/video/PublicationSection"
import ContainerSection from "../components/video/ContainerSection"
import VersionsSection from "../components/video/VersionsSection"
import {
  getVideo,
  updateVideo,
  setFourView,
  setStartTilt,
  uploadAvatar,
  startDiarization,
  clearDiarization,
  getDiarizationStatus,
  updateAttributionEdits,
  updateParticipants,
  uploadThumbnail,
  updateCueText,
  splitCue,
  joinCue,
  getLanguages,
  startTranslation,
  getTranslationStatus,
  resolveContainerForVideo,
  ContainerResolution,
} from "../system/apitools"
import { Participant, Container, Video } from "../types"
import useRouter from "../hooks/useRouter"
import useSnackbar from "../hooks/useSnackbar"


const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`

const PaneStack: FC<PropsWithChildren> = ({ children }) => (
  <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>{children}</Box>
)

// An unmet precondition has to say what it is. The rail promises every group
// exists whatever state the video is in, so rendering nothing would make the
// rail lie.
const PaneEmpty: FC<{ title: string; body: string }> = ({ title, body }) => (
  <Box
    sx={{
      bgcolor: ink.card,
      border: `1px dashed ${ink.ruleStrong}`,
      borderRadius: "8px",
      px: 4,
      py: 5,
      textAlign: "center",
    }}
  >
    <Typography sx={{ fontSize: "0.9375rem", fontWeight: 600, mb: 0.75 }}>{title}</Typography>
    <Typography sx={{ fontSize: "0.8125rem", color: ink.secondary, maxWidth: 440, mx: "auto" }}>
      {body}
    </Typography>
  </Box>
)

const RailFooter: FC<{ sealed: boolean; published: boolean }> = ({ sealed, published }) => {
  if (sealed) {
    return (
      <>
        <Typography sx={{ fontSize: "0.78125rem", fontWeight: 600, mb: 0.75 }}>Sealed</Typography>
        <Typography sx={{ fontSize: "0.71875rem", color: ink.tertiary, lineHeight: 1.45 }}>
          There is no unseal. To change anything, cut a new version.
        </Typography>
      </>
    )
  }
  if (!published) return null
  // No countdown: nothing seals a video on a timer. It stays replaceable until
  // the publisher seals it, as a separate, explicit act.
  return (
    <>
      <Typography sx={{ fontSize: "0.78125rem", fontWeight: 600, mb: 0.75 }}>Published, not sealed</Typography>
      <Typography sx={{ fontSize: "0.71875rem", color: ink.tertiary, lineHeight: 1.45 }}>
        It can still be replaced. Sealing is a separate step, and it can never be undone.
      </Typography>
    </>
  )
}

const VideoPage: FC = () => {
  const queryClient = useQueryClient()
  const router = useRouter()
  const snackbar = useSnackbar()
  const {
    params: { id },
  } = router

  const [name, setName] = useState("")
  const [description, setDescription] = useState("")
  const [series, setSeries] = useState("")
  const [episode, setEpisode] = useState("")
  const [videoLanguage, setVideoLanguage] = useState("en")
  const [participants, setParticipants] = useState<Participant[]>([])
  const [attributions, setAttributions] = useState<Record<string, string>>({})
  const [speakerLabels, setSpeakerLabels] = useState<Record<string, string>>({})
  const [attributionOverrides, setAttributionOverrides] = useState<Record<string, string>>({})
  const [subtitleOffset, setSubtitleOffset] = useState(0)
  const [diarizationProgress, setDiarizationProgress] = useState<{
    percent?: number
    stage?: string
    speed?: string
    logs?: string[]
  } | null>(null)
  const [translationProgress, setTranslationProgress] = useState<{
    percent?: number
    stage?: string
    logs?: string[]
  } | null>(null)
  const [rotationAdjustment, setRotationAdjustment] = useState(0)
  const [streamUrl, setStreamUrl] = useState("")
  const [subtitlesVersion, setSubtitlesVersion] = useState(0)

  // --- Queries ---

  const {
    isPending,
    error,
    data: video,
  } = useQuery({
    queryKey: ["video", id],
    queryFn: () => getVideo(id),
  })

  const { data: languages = [] } = useQuery({
    queryKey: ["languages"],
    queryFn: getLanguages,
  })

  // Every version's video ID resolves to the same container (see
  // data.VideoStore.ResolveContainer), so this works whichever version's
  // admin page is currently open. Resolves to null — not an error — for a
  // video that has never been published and so has no container yet.
  const { data: containerResolution } = useQuery({
    queryKey: ["container-for-video", id],
    queryFn: () => resolveContainerForVideo(id),
    enabled: !!id,
  })
  const container = containerResolution?.container

  const handleContainerChange = useCallback(
    (updated: Container) => {
      queryClient.setQueryData(
        ["container-for-video", id],
        (old: ContainerResolution | null | undefined) => (old ? { ...old, container: updated } : old),
      )
    },
    [id, queryClient],
  )

  // --- Sync form fields ---

  useEffect(() => {
    if (video) {
      setName(video.name)
      setDescription(video.description)
      setSeries(video.series || "")
      setEpisode(video.episode || "")
      setVideoLanguage(video.language || "en")
      setRotationAdjustment(video.rotation_adjustment || 0)
      setParticipants(video.participants || [])
      setAttributions(video.speaker_attributions || {})
      setSpeakerLabels(video.speaker_labels || {})
      setAttributionOverrides(video.attribution_overrides || {})
      setSubtitleOffset(video.subtitle_offset || 0)
      setStreamUrl(video.stream_url || "")
    }
  }, [video])

  // --- Polling effect for diarization ---

  useEffect(() => {
    if (video?.diarization_state !== "diarizing") return
    const poll = async () => {
      try {
        const status = await getDiarizationStatus(id)
        setDiarizationProgress({
          percent: status.percent,
          stage: status.stage,
          speed: status.speed,
          logs: status.logs,
        })
        if (status.state === "diarized" || status.state === "diarize_failed") {
          queryClient.invalidateQueries({ queryKey: ["video", id] })
          setDiarizationProgress(null)
        }
      } catch {
        /* ignore */
      }
    }
    poll()
    const interval = setInterval(poll, 1000)
    return () => clearInterval(interval)
  }, [video?.diarization_state, id, queryClient])

  // --- Polling effect for translation ---
  useEffect(() => {
    if (video?.translation_state !== "translating") return
    const poll = async () => {
      try {
        const status = await getTranslationStatus(id)
        setTranslationProgress({
          percent: status.percent,
          stage: status.stage,
          logs: status.logs,
        })
        if (status.state === "translated" || status.state === "translate_failed") {
          queryClient.invalidateQueries({ queryKey: ["video", id] })
          setTranslationProgress(null)
        }
      } catch {
        /* ignore */
      }
    }
    poll()
    const interval = setInterval(poll, 1000)
    return () => clearInterval(interval)
  }, [video?.translation_state, id, queryClient])

  // --- Notifications ---

  const savedTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const notifySaved = useCallback(() => {
    if (savedTimer.current) clearTimeout(savedTimer.current)
    savedTimer.current = setTimeout(() => snackbar.success("Saved"), 400)
  }, [snackbar])

  // --- Mutations ---

  const saveMutation = useMutation({
    mutationFn: (p: {
      name: string
      description: string
      participants: Participant[]
      rotationAdjustment: number
      streamUrl: string
      language: string
      series?: string
      episode?: string
    }) =>
      updateVideo(
        id,
        p.name,
        p.description,
        p.participants,
        p.rotationAdjustment,
        p.streamUrl,
        p.language,
        undefined, // ordering — a container field, never sent from here
        undefined, // private  — likewise
        p.series,
        p.episode,
      ),
    onSuccess: (updatedVideo) => {
      queryClient.setQueryData(["video", updatedVideo.id], updatedVideo)
      queryClient.invalidateQueries({ queryKey: ["video", updatedVideo.id] })
      queryClient.invalidateQueries({ queryKey: ["videos"] })
      notifySaved()
    },
    onError: (err: any) => snackbar.error(`Failed to save: ${err.message}`),
  })

  const fourViewMutation = useMutation({
    mutationFn: (framing: { paneAspect: number; zoom: number } | null) => setFourView(id, framing),
    onSuccess: (updatedVideo) => {
      queryClient.setQueryData(["video", updatedVideo.id], updatedVideo)
      notifySaved()
    },
    onError: (err: any) => snackbar.error(`Failed to save framing: ${err.response?.data?.error || err.message}`),
  })

  const startTiltMutation = useMutation({
    mutationFn: (degrees: number) => setStartTilt(id, degrees),
    onSuccess: (updatedVideo) => {
      queryClient.setQueryData(["video", updatedVideo.id], updatedVideo)
      notifySaved()
    },
    onError: (err: any) => snackbar.error(`Failed to save starting tilt: ${err.response?.data?.error || err.message}`),
  })

  const diarizeMutation = useMutation({
    mutationFn: () => startDiarization(id),
    onSuccess: (updatedVideo) => {
      queryClient.setQueryData(["video", updatedVideo.id], updatedVideo)
      queryClient.invalidateQueries({ queryKey: ["video", updatedVideo.id] })
      snackbar.success("Diarization started")
    },
    onError: (err: any) =>
      snackbar.error(`Failed to start diarization: ${err.message}`),
  })

  const translateMutation = useMutation({
    mutationFn: (langs: string[]) => startTranslation(id, langs),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["video", id] })
      snackbar.success("Translation started")
    },
    onError: (err: Error) =>
      snackbar.error(`Failed to start translation: ${err.message}`),
  })

  const clearDiarizationMutation = useMutation({
    mutationFn: () => clearDiarization(id),
    onSuccess: (updatedVideo) => {
      queryClient.setQueryData(["video", updatedVideo.id], updatedVideo)
      queryClient.invalidateQueries({ queryKey: ["video", updatedVideo.id] })
      queryClient.invalidateQueries({ queryKey: ["subtitles", id] })
      setAttributions({})
      setSpeakerLabels({})
      setAttributionOverrides({})
      snackbar.success("Diarization cleared")
    },
    onError: (err: any) =>
      snackbar.error(`Failed to clear diarization: ${err.message}`),
  })

  const editsMutation = useMutation({
    mutationFn: (payload: { labels: Record<string, string>; overrides: Record<string, string> }) =>
      updateAttributionEdits(id, payload.labels, payload.overrides),
    onSuccess: (updatedVideo) => {
      queryClient.setQueryData(["video", updatedVideo.id], updatedVideo)
      setAttributions(updatedVideo.speaker_attributions || {})
      notifySaved()
    },
    onError: (err: any) => snackbar.error(`Failed to save attribution: ${err.message}`),
  })

  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const scheduleSave = useCallback(
    (labels: Record<string, string>, overrides: Record<string, string>) => {
      if (saveTimer.current) clearTimeout(saveTimer.current)
      saveTimer.current = setTimeout(() => editsMutation.mutate({ labels, overrides }), 600)
    },
    [editsMutation],
  )

  const participantsMutation = useMutation({
    mutationFn: (ps: Participant[]) => updateParticipants(id, ps),
    onSuccess: (updatedVideo) => {
      queryClient.setQueryData(["video", updatedVideo.id], updatedVideo)
      notifySaved()
    },
    onError: (err: any) => snackbar.error(`Failed to save speakers: ${err.message}`),
  })

  const participantsSaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null)
  const scheduleParticipantsSave = useCallback(
    (ps: Participant[]) => {
      if (participantsSaveTimer.current) clearTimeout(participantsSaveTimer.current)
      participantsSaveTimer.current = setTimeout(() => participantsMutation.mutate(ps), 600)
    },
    [participantsMutation],
  )

  const editCueMutation = useMutation({
    mutationFn: (p: { index: number; text: string; lang: string }) => updateCueText(id, p.index, p.text, p.lang),
    onSuccess: (updatedVideo) => {
      queryClient.setQueryData(["video", updatedVideo.id], updatedVideo)
      setSubtitlesVersion((v) => v + 1)
      notifySaved()
    },
    onError: (err: any) => snackbar.error(`Failed to save line: ${err.message}`),
  })

  const handleEditCue = useCallback(
    (index: number, text: string, lang: string) => editCueMutation.mutate({ index, text, lang }),
    [editCueMutation],
  )

  // A split or join renumbers lines, so everything keyed by line number is
  // taken from the server's answer rather than patched locally.
  const onRestructured = useCallback(
    (updatedVideo: Video) => {
      queryClient.setQueryData(["video", updatedVideo.id], updatedVideo)
      setAttributions(updatedVideo.speaker_attributions || {})
      setAttributionOverrides(updatedVideo.attribution_overrides || {})
      setSubtitlesVersion((v) => v + 1)
      notifySaved()
    },
    [queryClient, notifySaved],
  )

  const splitCueMutation = useMutation({
    mutationFn: (p: { index: number; word: number }) => splitCue(id, p.index, p.word),
    onSuccess: onRestructured,
    onError: (err: any) => snackbar.error(`Failed to split line: ${err.response?.data?.error || err.message}`),
  })

  const joinCueMutation = useMutation({
    mutationFn: (index: number) => joinCue(id, index),
    onSuccess: onRestructured,
    onError: (err: any) => snackbar.error(`Failed to join line: ${err.response?.data?.error || err.message}`),
  })

  // One line's speaker, as a per-line override on top of its voice group.
  const handleSetLineSpeaker = useCallback(
    (index: number, participantId: string) => {
      const overrides = { ...attributionOverrides, [String(index)]: participantId }
      setAttributionOverrides(overrides)
      setAttributions((prev) => ({ ...prev, [String(index)]: participantId }))
      scheduleSave(speakerLabels, overrides)
    },
    [attributionOverrides, speakerLabels, scheduleSave],
  )

  const handleParticipantsChange = useCallback(
    (next: Participant[]) => {
      setParticipants(next)
      scheduleParticipantsSave(next)
    },
    [scheduleParticipantsSave],
  )

  const commitVideoFields = useCallback(
    (overrides?: Partial<VideoLevelFields>) => {
      if (!video) return
      const fields: VideoLevelFields = {
        name,
        description,
        series,
        episode,
        rotationAdjustment,
        streamUrl,
        language: videoLanguage,
        ...overrides,
      }
      if (!shouldCommitVideoFields(fields, video)) return
      saveMutation.mutate({
        name: fields.name,
        description: fields.description,
        participants,
        rotationAdjustment: fields.rotationAdjustment,
        streamUrl: fields.streamUrl,
        language: fields.language,
        series: fields.series,
        episode: fields.episode,
      })
    },
    [video, name, description, series, episode, videoLanguage, rotationAdjustment, streamUrl, participants, saveMutation],
  )

  useEffect(() => () => {
    if (saveTimer.current) clearTimeout(saveTimer.current)
    if (participantsSaveTimer.current) clearTimeout(participantsSaveTimer.current)
    if (savedTimer.current) clearTimeout(savedTimer.current)
  }, [])

  // --- Callbacks ---

  const handleAvatarUpload = useCallback(
    async (pid: string, file: File) => {
      try {
        const updatedVideo = await uploadAvatar(id, pid, file)
        queryClient.setQueryData(["video", id], updatedVideo)
        queryClient.invalidateQueries({ queryKey: ["video", id] })
        snackbar.success("Avatar uploaded")
      } catch (err: any) {
        snackbar.error(`Avatar upload failed: ${err.message}`)
      }
    },
    [id, queryClient, snackbar],
  )

  const handleThumbnailUpload = useCallback(
    async (file: File) => {
      try {
        const updatedVideo = await uploadThumbnail(id, file)
        queryClient.setQueryData(["video", updatedVideo.id], updatedVideo)
        queryClient.invalidateQueries({ queryKey: ["video", updatedVideo.id] })
        snackbar.success("Thumbnail uploaded")
      } catch (err: any) {
        snackbar.error(`Thumbnail upload failed: ${err.message}`)
      }
    },
    [id, queryClient, snackbar],
  )

  const handleRelabelVoice = useCallback(
    (tag: string, participantId: string) => {
      const newLabels = { ...speakerLabels, [tag]: participantId }
      setSpeakerLabels(newLabels)
      const group = video?.voice_groups?.find((g) => g.tag === tag)
      if (group) {
        setAttributions((prev) => {
          const next = { ...prev }
          for (const idx of group.cue_indices) {
            if (attributionOverrides[String(idx)] === undefined) next[String(idx)] = participantId
          }
          return next
        })
      }
      scheduleSave(newLabels, attributionOverrides)
    },
    [speakerLabels, attributionOverrides, video, scheduleSave],
  )

  // --- Derived state ---

  const participantsWithAvatarUrls = useMemo(
    () =>
      participants.map((p) => ({
        ...p,
        avatar_url: p.avatar_url
          ? `/api/v1/videos/${id}/participants/${p.id}/avatar/image?v=${encodeURIComponent(p.avatar_url)}`
          : undefined,
      })),
    [participants, id],
  )

  if (isPending) return <Loading />
  if (error) return <ErrorComponent message={error.message} />
  if (!video) return <ErrorComponent message="Video not found" />

  const subtitleContentUrl = video.subtitles
    ? `/api/v1/videos/${id}/subtitles/content?v=${subtitlesVersion}`
    : undefined

  const sealed = isVideoSealed(video)

  const pub = video.publication
  const revision = pub?.revision ?? 0
  const langCount = video.subtitles?.languages?.length ?? 0
  const speakerCount = participants.length
  const jobs = video.job_history ?? []
  const jobsFailed = jobs.some((j) => j.status === "failed")
  const diarising = video.diarization_state === "diarizing"
  const translating = video.translation_state === "translating"
  const publishing = video.publication_state === "publishing"
  const publicPath = `/video/${container?.slug || id}`

  // --- Rail summaries. Each group answers "what state is this in?" without
  //     being opened, which is the whole reason the rail exists. ---

  const contentStatus: PaneStatus = sealed
    ? "locked"
    : video.state === "ready"
      ? "ok"
      : "warn"
  const contentSummary = video.state === "ready"
    ? `${plural(speakerCount, "speaker")}, stream ready`
    : "Needs a stream URL"

  // Degrees as a viewer would read them; the sign says which way.
  const tiltNow = video.start_tilt ?? 0
  const cameraParts = [
    rotationAdjustment ? `turned ${rotationAdjustment}°` : null,
    tiltNow ? `tilted ${tiltNow}°` : null,
  ].filter(Boolean)
  const cameraSummary = video.state !== "ready"
    ? "Needs a stream URL"
    : cameraParts.length > 0 ? `Starts ${cameraParts.join(", ")}` : "Starts level, facing north"

  let subtitlesStatus: PaneStatus = "idle"
  let subtitlesSummary = "No subtitles yet"
  if (diarising || translating) {
    subtitlesStatus = "busy"
    subtitlesSummary = diarising ? "Diarising now" : "Translating now"
  } else if (video.diarization_state === "diarize_failed" || video.translation_state === "translate_failed") {
    subtitlesStatus = "warn"
    subtitlesSummary =
      video.diarization_state === "diarize_failed" ? "Diarisation failed" : "Translation failed"
  } else if (langCount > 0) {
    subtitlesStatus = sealed ? "locked" : "ok"
    subtitlesSummary = plural(langCount, "language")
  } else if (sealed) {
    subtitlesStatus = "locked"
  }

  let publishingStatus: PaneStatus = "idle"
  let publishingSummary = "Not published yet"
  if (publishing) {
    publishingStatus = "busy"
    publishingSummary = "Hashing segments"
  } else if (video.publication_state === "publish_failed") {
    publishingStatus = "warn"
    publishingSummary = "Publish failed"
  } else if (sealed) {
    publishingStatus = "ok"
    publishingSummary = `v${revision}, sealed`
  } else if (pub) {
    publishingStatus = "warn"
    publishingSummary = `v${revision}, not sealed`
  }

  const panes: AdminPane[] = [
    {
      id: "content",
      label: "Content",
      summary: contentSummary,
      status: contentStatus,
      content: (
        <PaneStack>
          <VideoDetailsSection
            name={name}
            description={description}
            series={series}
            episode={episode}
            participants={participants}
            videoId={id}
            onNameChange={setName}
            onDescriptionChange={setDescription}
            onSeriesChange={setSeries}
            onEpisodeChange={setEpisode}
            onParticipantsChange={handleParticipantsChange}
            onAvatarUpload={handleAvatarUpload}
            thumbnailUrl={video.thumbnail_url}
            onThumbnailUpload={handleThumbnailUpload}
            onCommit={() => commitVideoFields()}
            language={videoLanguage}
            languages={languages}
            onLanguageChange={(lang) => {
              setVideoLanguage(lang)
              commitVideoFields({ language: lang })
            }}
            sealed={sealed}
          />
          <StreamUrlsSection
            video={video}
            streamUrl={streamUrl}
            onStreamUrlChange={setStreamUrl}
            onCommit={() => commitVideoFields()}
            subtitleContentUrl={subtitleContentUrl}
            participants={participantsWithAvatarUrls}
            attributions={attributions}
            subtitleOffset={subtitleOffset}
            rotationAdjustment={rotationAdjustment}
          />
        </PaneStack>
      ),
    },
    {
      id: "camera",
      label: "Camera",
      summary: cameraSummary,
      status: video.state === "ready" ? "ok" : "idle",
      content: (
        <PaneStack>
          {video.state !== "ready" ? (
            <PaneEmpty
              title="The camera needs a stream first"
              body="Paste the 360° stream URL under Content and save. The previews here play from it."
            />
          ) : (
            <>
              <StartPositionSection
                video={video}
                participants={participantsWithAvatarUrls}
                rotationAdjustment={rotationAdjustment}
                sealed={sealed}
                onSaveTilt={(degrees) => startTiltMutation.mutate(degrees)}
                onSaveRotation={(degrees) => {
                  setRotationAdjustment(degrees)
                  commitVideoFields({ rotationAdjustment: degrees })
                }}
              />
              <FourViewSection
                video={video}
                participants={participantsWithAvatarUrls}
                rotationAdjustment={rotationAdjustment}
                onSave={(framing) => fourViewMutation.mutate(framing)}
              />
            </>
          )}
        </PaneStack>
      ),
    },
    {
      id: "subtitles",
      label: "Subtitles",
      summary: subtitlesSummary,
      status: subtitlesStatus,
      content: (
        <PaneStack>
          {/* The empty states are deliberate. The rail promises this group
              exists whatever the video's state, so an unmet precondition has
              to say what it is rather than render nothing. */}
          {video.state !== "ready" ? (
            <PaneEmpty
              title="Subtitles need a stream first"
              body="Paste the 360° stream URL under Content and save. Diarisation reads the audio from that playlist."
            />
          ) : speakerCount === 0 ? (
            <PaneEmpty
              title="Add the speakers first"
              body="Diarisation finds distinct voices, but it needs a list of people to attribute them to. Add them under Content."
            />
          ) : (
            <DiarizationSection
              video={video}
              participants={participantsWithAvatarUrls}
              subtitleContentUrl={subtitleContentUrl}
              subtitleOffset={subtitleOffset}
              rotationAdjustment={rotationAdjustment}
              speakerLabels={speakerLabels}
              attributions={attributions}
              diarizationProgress={diarizationProgress}
              onStartDiarization={() => diarizeMutation.mutate()}
              isStarting={diarizeMutation.isPending}
              onClearDiarization={() => clearDiarizationMutation.mutate()}
              isClearing={clearDiarizationMutation.isPending}
              onRelabelVoice={handleRelabelVoice}
              onEditCue={handleEditCue}
              onSplitCue={(index, word) => splitCueMutation.mutate({ index, word })}
              onJoinCue={(index) => joinCueMutation.mutate(index)}
              onSetLineSpeaker={handleSetLineSpeaker}
              isReady={video.state === "ready"}
              sealed={sealed}
            />
          )}

          {video.state === "ready" && video.subtitles && (
            <TranslationsSection
              video={video}
              languages={languages}
              translationProgress={translationProgress}
              onTranslate={(langs) => translateMutation.mutate(langs)}
              isStarting={translateMutation.isPending}
              sealed={sealed}
            />
          )}

          {/* Addendum — the mirror image of Translations: hidden until sealed,
              since before that a new language belongs in the base via a
              normal translate + republish, not a standalone addendum. */}
          {sealed && <AddendumSection video={video} languages={languages} />}
        </PaneStack>
      ),
    },
    {
      id: "publishing",
      label: "Publishing",
      summary: publishingSummary,
      status: publishingStatus,
      content: (
        <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start", flexWrap: { xs: "wrap", lg: "nowrap" } }}>
          {/* Left — the publication itself and its version history: the record
              of what was sealed and when. */}
          <Box sx={{ flexGrow: 1, minWidth: 320, display: "flex", flexDirection: "column", gap: 2 }}>
            <PublicationSection video={video} isReady={video.state === "ready"} />

            {/* Versions — the container's publication history. A claimed entry
                (one the chain already has a VersionRef for) is append-only; an
                unclaimed one can still be deleted outright and the list
                renumbered, whether or not the container itself is anchored (see
                VersionsSection.tsx's comment for the full rule). */}
            {container && (
              <VersionsSection
                container={container}
                sourceVideoId={id}
                onContainerChange={handleContainerChange}
              />
            )}
          </Box>

          {/* Right — how the video is found and shared: its listing identity
              and the embed snippet. Narrower, and secondary to the record. */}
          <Box sx={{ flex: "0 0 384px", maxWidth: "100%", display: "flex", flexDirection: "column", gap: 2 }}>
            {/* Container settings — slug, ordering, private. Every video gets a
                container the moment it is created (CreateVideo calls
                EnsureContainer), so this is normally present from the start; the
                guard covers a video whose container creation failed and one
                being viewed before the boot migration reached it. */}
            {container && (
              <ContainerSection container={container} onContainerChange={handleContainerChange} />
            )}

            {video.state === "ready" && (
              <EmbedCodeSection
                videoId={id}
                videoName={video.name}
                thumbnailUrl={video.thumbnail_url}
                baseUrl={window.location.origin}
              />
            )}
          </Box>
        </Box>
      ),
    },
    {
      id: "activity",
      label: "Activity",
      summary: jobs.length > 0 ? plural(jobs.length, "job") : "No jobs yet",
      status: jobsFailed ? "warn" : "idle",
      content: (
        <PaneStack>
          {jobs.length > 0 ? (
            <JobHistorySection jobHistory={jobs} />
          ) : (
            <PaneEmpty
              title="Nothing has run yet"
              body="Diarisation, translation and publishing all record what they did here, with timings and any error."
            />
          )}
        </PaneStack>
      ),
    },
  ]

  return (
    <AdminShell
      title={video.name || "Untitled video"}
      chips={
        <>
          {sealed ? (
            <Chip
              size="small"
              label="Sealed"
              sx={{ height: 24, bgcolor: ink.primary, color: "#fff" }}
            />
          ) : pub ? (
            <Chip
              size="small"
              label={`Published v${revision}`}
              sx={{ height: 24, bgcolor: ink.jadeTint, color: ink.jadeText }}
            />
          ) : (
            <Chip
              size="small"
              label="Draft"
              sx={{ height: 24, bgcolor: ink.neutralTint, color: ink.secondary }}
            />
          )}
          {video.state !== "ready" && (
            <Chip
              size="small"
              label="Awaiting stream URL"
              sx={{ height: 24, bgcolor: ink.alarmTint, color: ink.alarmText }}
            />
          )}
        </>
      }
      subtitle={
        <Typography
          sx={{
            fontFamily: '"IBM Plex Mono", ui-monospace, monospace',
            fontSize: "0.75rem",
            color: ink.tertiary,
          }}
        >
          {window.location.host}{publicPath}
        </Typography>
      }
      actions={
        <Button
          variant="outlined"
          size="small"
          href={publicPath}
          target="_blank"
          rel="noopener"
          sx={{ borderColor: ink.ruleStrong, color: ink.primary }}
        >
          Open public page
        </Button>
      }
      railFooter={<RailFooter sealed={sealed} published={!!pub} />}
      onBack={() => router.navigate("admin-videos")}
      panes={panes}
    />
  )
}

export default VideoPage
